import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---- Suppress Create-React-App's dev-time full-screen red error overlay ----
// We ship our own friendly bottom-right popup (ErrorBoundary) which auto-refreshes
// in 5 seconds. The CRA overlay is only useful for local debugging and looks
// alarming to end users viewing a preview build.
if (typeof window !== "undefined") {
  const hideOverlay = () => {
    document.querySelectorAll("body > iframe").forEach((el) => {
      const s = el.getAttribute("style") || "";
      if (s.includes("2147483647") || (el.id || "").includes("webpack")) {
        el.remove();
      }
    });
  };
  // Runs once + on every DOM insertion (CRA re-mounts the iframe per error).
  hideOverlay();
  try {
    new MutationObserver(hideOverlay).observe(document.documentElement, { childList: true, subtree: true });
  } catch { /* ignore */ }
  // Prevent unhandled promise rejections from triggering the overlay while still logging them.
  window.addEventListener("unhandledrejection", (e) => {
    // eslint-disable-next-line no-console
    console.warn("Unhandled promise rejection (suppressed overlay):", e.reason);
    e.preventDefault();
  });
  window.addEventListener("error", (e) => {
    // Keep the console log, but stop the overlay iframe from popping up.
    if (e?.error) {
      // eslint-disable-next-line no-console
      console.warn("Runtime error (suppressed overlay):", e.error);
    }
    // Note: we cannot preventDefault() here because it would swallow real errors
    // from React's render pipeline; our MutationObserver removes the iframe instead.
  }, true);
}


// ---- Browser PWA install lifecycle ---------------------------------------
// Chromium fires beforeinstallprompt only after all installability criteria
// are met. Capture it once globally so Settings can expose a real Install
// button even if the event fired before that panel mounted. iOS intentionally
// has no equivalent event and is handled with Share → Add to Home Screen copy.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    window.__slxPwaInstallPrompt = event;
    window.dispatchEvent(new CustomEvent("slx-pwa-installable", { detail: event }));
  });
  window.addEventListener("appinstalled", () => {
    window.__slxPwaInstallPrompt = null;
    window.dispatchEvent(new CustomEvent("slx-pwa-installed"));
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// ---- PWA service worker registration ----
// Only in production builds — CRA's dev server ships its own SW that
// interferes with hot reload. `service-worker.js` implements a
// network-first HTML / cache-first assets / no-cache API strategy.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js", { updateViaCache: "none" })
      .then((registration) => {
        const inspect = () => {
          const worker = registration.installing;
          if (!worker) return;
          worker.addEventListener("statechange", () => {
            if (worker.state === "installed" && navigator.serviceWorker.controller) {
              window.__slxWaitingRegistration = registration;
              window.dispatchEvent(new CustomEvent("slx-app-update-ready", { detail: { registration } }));
            }
          });
        };
        registration.addEventListener("updatefound", inspect);
        inspect();
        if (registration.waiting && navigator.serviceWorker.controller) {
          window.__slxWaitingRegistration = registration;
              window.dispatchEvent(new CustomEvent("slx-app-update-ready", { detail: { registration } }));
        }
        registration.update().catch(() => {});
        const timer = window.setInterval(() => registration.update().catch(() => {}), 15 * 60 * 1000);
        window.addEventListener("beforeunload", () => window.clearInterval(timer), { once: true });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[pwa] service worker registration failed:", err);
      });
  });
}
